import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  type ThemePreference,
  type ResolvedTheme,
  applyTheme,
  getStoredThemePreference,
  resolveTheme,
  storeThemePreference,
  systemPrefersDark,
} from './theme';
import { parseEmbedUrlParams } from './embed/route';

interface ThemeContextValue {
  /** The user's choice: light, dark, or auto (follow the OS). */
  preference: ThemePreference;
  /** The concrete theme currently applied (auto resolved against the OS). */
  resolvedTheme: ResolvedTheme;
  /** Set and persist the preference; takes effect immediately. */
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * App-wide theme state. The pre-paint inline script in index.html sets the
 * initial `data-theme` to avoid a flash; this provider then owns it: it
 * re-applies on preference change and, while on `auto`, live-tracks the OS
 * via prefers-color-scheme. Preference persists to localStorage.
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [embedTheme] = useState(() => parseEmbedUrlParams(new URLSearchParams(window.location.search))?.theme);
  const [preference, setPref] = useState<ThemePreference>(() => getStoredThemePreference());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Track the OS preference. Only changes the resolved theme while on `auto`,
  // but we always listen so flipping back to auto is instantly correct.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme = embedTheme ?? resolveTheme(preference, systemDark);

  // Keep <html data-theme> in sync with the resolved theme.
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setPreference = useCallback((next: ThemePreference) => {
    storeThemePreference(next);
    setPref(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, resolvedTheme, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
};

/** Access the theme preference and setter. Throws outside a ThemeProvider. */
export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};
